import React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { invalidateSession } from 'redux-simple-auth';
import { Button, Card, CardContent, Typography } from '@material-ui/core';

import { ApplicationState } from '../reducers';
import { getSessionHomePortal, Portal } from '../selectors/portal';

// Human-readable name for each portal, used in the access-denied copy so the
// user understands where they *should* be.
export function portalLabel(portal: Portal): string {
    switch (portal) {
        case 'scheduler':
            return 'LSV Scheduler';
        case 'network':
            return 'Network Admin';
        case 'bims':
            return 'BIMS (employer) ';
    }
}

interface IViewProps {
    // The portal this session legitimately belongs to, or null when the
    // session has no bims-web portal at all (global staff / applicants).
    homePortal: Portal | null;
    onReturn: () => void;
}

// Presentational interstitial shown when an authenticated session lands on a
// portal it is not allowed to use. Rendering this *instead of* the portal's
// route Switch is what prevents the escalation: none of the portal screens
// mount. The button signs the session out and returns to this origin's login.
export function PortalRedirectView(props: IViewProps) {
    const { homePortal, onReturn } = props;
    const message = homePortal
        ? `Your account belongs to the ${portalLabel(homePortal).trim()} portal. ` +
          'Please sign in there instead.'
        : "You don't have access to this portal with this account.";
    return (
        <div style={{ maxWidth: 480, margin: '15vh auto', padding: 16 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Access denied
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        {message}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onReturn}
                        style={{ marginTop: 16 }}
                    >
                        Return to login
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

interface IProps {
    homePortal: Portal | null;
    dispatch: any;
}

interface IState {
    done: boolean;
}

class PortalRedirect extends React.PureComponent<IProps, IState> {
    state: IState = { done: false };

    handleReturn = () => {
        const { dispatch } = this.props;
        dispatch(invalidateSession())
            .catch(() => undefined)
            .then(() => this.setState({ done: true }));
        // Optimistically flip so the redirect fires even if the promise is
        // swallowed; once unauthenticated, Routes renders this origin's
        // anonymous entry (login / landing).
        this.setState({ done: true });
    };

    render() {
        if (this.state.done) {
            return <Redirect to={'/'} />;
        }
        return (
            <PortalRedirectView
                homePortal={this.props.homePortal}
                onReturn={this.handleReturn}
            />
        );
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    homePortal: getSessionHomePortal(state),
});

export default connect(mapStateToProps)(PortalRedirect);
